import {
  authenticatedFetch,
  extractApiErrorMessage,
  resolvePeriverseApiBaseUrl,
} from '../shared';
import {
  PaginatedSurgeonsResponseDtoSchema,
  SurgeonDtoSchema,
  type CreateSurgeonDto,
  type PaginatedSurgeonsResponseDto,
  type SurgeonDto,
  type SurgeonsFilterParams,
  type UpdateSurgeonDto,
} from '@peritasai/types';

type BaseUrlResolver = () => string;

export class SurgeonsApiClient {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  async getSurgeons(
    options: SurgeonsFilterParams = {}
  ): Promise<PaginatedSurgeonsResponseDto> {
    const queryParams = new URLSearchParams();

    if (options.search) {
      queryParams.append('search', options.search);
    }

    const page = options.page !== undefined ? options.page + 1 : 1;
    const limit = options.limit ?? 10;
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    const url = `${this.resolveBaseUrl()}/surgeons?${queryParams.toString()}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return PaginatedSurgeonsResponseDtoSchema.parse(await response.json());
  }

  async createSurgeon(dto: CreateSurgeonDto): Promise<SurgeonDto> {
    const url = `${this.resolveBaseUrl()}/surgeons`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return SurgeonDtoSchema.parse(await response.json());
  }

  async updateSurgeon(id: string, dto: UpdateSurgeonDto): Promise<SurgeonDto> {
    const url = `${this.resolveBaseUrl()}/surgeons/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return SurgeonDtoSchema.parse(await response.json());
  }
}

export const createSurgeonsApiClient = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): SurgeonsApiClient => new SurgeonsApiClient(resolveBaseUrl);

export const surgeonsApiClient = new SurgeonsApiClient();

export type { BaseUrlResolver };
